import { delay } from "./common";
import { getSyncInferenceApiUrl } from "../../constants/ApiUrl";
import { EndpointType } from "../../constants/Endpoints";
import { DEMO_MODE_FEATURE, FEATURE } from "../../constants/FeatureFlag";
import { MERGED_FILES_NAME, UPLOAD_DELAY_MS } from "../../constants/upload";
import { InferenceConfig, OacJsonResponse } from "../../types/demo";
import { createHeaders, createInferenceFormData } from "../../utils/api";
import { getConsoleCsrfToken } from "../../utils/csrf";
import { assertFilesSelected } from "../../utils/file";
import { endpointStore } from "../../utils/mobx/EndpointStore";
import { envVarStore } from "../../utils/mobx/EnvVarStore";

export const imageSyncUploader = async (
  selectedFiles: File[],
  url: string,
  type: string,
  serviceName: string,
  inferenceConfig?: InferenceConfig,
): Promise<[OacJsonResponse, string][]> => {
  assertFilesSelected(selectedFiles);

  const { endpointType, accessToken, userSettings, sessionId } = endpointStore;
  const { backendUrl, multifileSingleRequest } = envVarStore;
  const logEnabled = userSettings.logging_config.api_log_enabled;
  const apiUrl = getSyncInferenceApiUrl(backendUrl, endpointType, accessToken);
  const csrfToken = await getCsrfTokenIfNeeded();

  if (multifileSingleRequest === "false") {
    return await uploadFilesSyncSequentially(
      selectedFiles,
      url,
      type,
      serviceName,
      endpointType,
      inferenceConfig,
      accessToken,
      logEnabled,
      sessionId,
      csrfToken,
      apiUrl,
    );
  } else {
    return await uploadFilesSyncAsBatch(
      selectedFiles,
      url,
      type,
      serviceName,
      endpointType,
      inferenceConfig,
      accessToken,
      logEnabled,
      sessionId,
      csrfToken,
      apiUrl,
    );
  }
};

const getCsrfTokenIfNeeded = async (): Promise<string> => {
  if (FEATURE === DEMO_MODE_FEATURE) {
    const { token } = await getConsoleCsrfToken();
    return token;
  }
  return "";
};

const uploadFilesSyncSequentially = async (
  files: File[],
  url: string,
  type: string,
  serviceName: string,
  endpointType: EndpointType,
  inferenceConfig: InferenceConfig | undefined,
  accessToken: string,
  logEnabled: boolean,
  sessionId: string,
  csrfToken: string,
  apiUrl: string,
): Promise<[OacJsonResponse, string][]> => {
  const results: [OacJsonResponse, string][] = [];

  for (const userFile of files) {
    const result = await uploadSingleFileSync(
      userFile,
      url,
      type,
      serviceName,
      endpointType,
      inferenceConfig,
      accessToken,
      logEnabled,
      sessionId,
      csrfToken,
      apiUrl,
    );
    results.push([result, userFile.name]);
    await delay(UPLOAD_DELAY_MS);
  }

  return results;
};

const uploadFilesSyncAsBatch = async (
  files: File[],
  url: string,
  type: string,
  serviceName: string,
  endpointType: EndpointType,
  inferenceConfig: InferenceConfig | undefined,
  accessToken: string,
  logEnabled: boolean,
  sessionId: string,
  csrfToken: string,
  apiUrl: string,
): Promise<[OacJsonResponse, string][]> => {
  const result = await uploadSingleFileSync(
    files,
    url,
    type,
    serviceName,
    endpointType,
    inferenceConfig,
    accessToken,
    logEnabled,
    sessionId,
    csrfToken,
    apiUrl,
  );
  return [[result, MERGED_FILES_NAME]];
};

const uploadSingleFileSync = async (
  fileOrFiles: File | File[],
  url: string,
  type: string,
  serviceName: string,
  endpointType: EndpointType,
  inferenceConfig: InferenceConfig | undefined,
  accessToken: string,
  logEnabled: boolean,
  sessionId: string,
  csrfToken: string,
  apiUrl: string,
): Promise<OacJsonResponse> => {
  const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
  const fd = createInferenceFormData(
    files,
    url,
    type,
    serviceName,
    endpointType,
    inferenceConfig,
  );

  const response = await fetch(apiUrl, {
    method: "POST",
    headers: createHeaders(accessToken, logEnabled, sessionId, csrfToken),
    body: fd,
  });

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const error = await response.json();
      // FastAPI HTTPException 형식: { detail: "..." }
      // 또는 SaaS 형식: { message: { en: "..." } }
      errorMessage = error.detail || error.message?.en || JSON.stringify(error);
    } catch {
      errorMessage = response.statusText || errorMessage;
    }
    throw new Error(`Failed to call sync inference: ${errorMessage}`);
  }

  return (await response.json()) as OacJsonResponse;
};
